// ============================================================
// Chess Arena — Spawn System
// ============================================================
// Dual spawn timer system (player + enemy).
// Each side has an independent fill bar; when full the next
// piece auto-places on a random empty back-row cell.
// ============================================================

import {
  GridPos,
  PieceType,
  Team,
  SpawnConfig,
  SpawnWeights,
  LevelConfig,
  PIECE_CONFIGS,
  BOARD_COLS,
  BOARD_ROWS,
} from '../core/types';
import eventBus from '../core/eventBus';

// -----------------------------------------------------------------
// Callback types — the spawn system doesn't own the board; it
// delegates placement and eligibility queries to the caller.
// -----------------------------------------------------------------

export interface SpawnCallbacks {
  /** Return true if the cell is empty & passable (no piece, no rock). */
  isCellAvailable: (pos: GridPos) => boolean;
  /** Place the piece on the board and return its new id. */
  placePiece: (type: PieceType, team: Team, pos: GridPos) => string;
}

// -----------------------------------------------------------------
// Internal timer state for one side
// -----------------------------------------------------------------

interface SideSpawn {
  team: Team;
  config: SpawnConfig;
  elapsed: number;       // seconds accumulated toward next spawn
  nextPieceType: PieceType | null;
  blocked: boolean;      // true when bar is full but no cells available
  pulseTime: number;     // accumulator for blocked-pulse animation
}

// -----------------------------------------------------------------
// SpawnSystem
// -----------------------------------------------------------------

export class SpawnSystem {
  private scene: Phaser.Scene;
  private player!: SideSpawn;
  private enemy!: SideSpawn;
  private currentLevel = 1;
  private callbacks!: SpawnCallbacks;
  private active = false;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ------------------------------------------------------------------
  // Lifecycle
  // ------------------------------------------------------------------

  init(levelConfig: LevelConfig, callbacks: SpawnCallbacks): void {
    this.callbacks = callbacks;
    this.currentLevel = levelConfig.level;

    this.player = this.makeSide('player', levelConfig.playerSpawn);
    this.enemy = this.makeSide('enemy', levelConfig.enemySpawn);

    this.active = true;
  }

  reset(): void {
    this.active = false;
    this.player = this.makeSide('player', { timerSeconds: 6, weights: {} });
    this.enemy = this.makeSide('enemy', { timerSeconds: 8, weights: {} });
  }

  // ------------------------------------------------------------------
  // Frame update
  // ------------------------------------------------------------------

  update(delta: number): void {
    if (!this.active) return;
    const dt = delta / 1000; // ms → seconds
    this.updateSide(this.player, dt);
    this.updateSide(this.enemy, dt);
  }

  private updateSide(side: SideSpawn, dt: number): void {
    // If next piece not yet decided, roll now (start of bar fill)
    if (!side.nextPieceType) {
      side.nextPieceType = this.rollPieceType(side.config.weights);
    }

    if (side.blocked) {
      // Bar is full but no valid cell — keep checking
      side.pulseTime += dt;
      const cell = this.findSpawnCell(side.team);
      if (cell) {
        this.doSpawn(side, cell);
      }
      return;
    }

    side.elapsed += dt;

    if (side.elapsed >= side.config.timerSeconds) {
      const cell = this.findSpawnCell(side.team);
      if (cell) {
        this.doSpawn(side, cell);
      } else {
        side.blocked = true;
        side.pulseTime = 0;
      }
    }
  }

  // ------------------------------------------------------------------
  // Spawning
  // ------------------------------------------------------------------

  private doSpawn(side: SideSpawn, cell: GridPos): void {
    const type = side.nextPieceType!;
    const id = this.callbacks.placePiece(type, side.team, cell);

    // Spawn animation: scale-up + glow
    this.playSpawnAnimation(cell);

    eventBus.emit('piece_spawned', {
      pieceId: id,
      type,
      team: side.team,
      pos: { col: cell.col, row: cell.row },
    });

    eventBus.emit('spawn_ready', { team: side.team });

    // Reset side timer
    side.elapsed = 0;
    side.blocked = false;
    side.pulseTime = 0;
    side.nextPieceType = null; // will be rolled on next frame
  }

  private playSpawnAnimation(pos: GridPos): void {
    // Lightweight tween: flash a circle at the cell
    const cellSize = 80; // will be overridable later
    const x = pos.col * cellSize + cellSize / 2;
    const y = pos.row * cellSize + cellSize / 2;

    const glow = this.scene.add.circle(x, y, cellSize * 0.45, 0xffff00, 0.5);
    glow.setDepth(10);

    this.scene.tweens.add({
      targets: glow,
      scaleX: 1.6,
      scaleY: 1.6,
      alpha: 0,
      duration: 400,
      ease: 'Quad.easeOut',
      onComplete: () => glow.destroy(),
    });
  }

  // ------------------------------------------------------------------
  // Cell selection
  // ------------------------------------------------------------------

  private findSpawnCell(team: Team): GridPos | null {
    const rows = team === 'player' ? [BOARD_ROWS - 2, BOARD_ROWS - 1] : [0, 1];
    const candidates: GridPos[] = [];

    for (const row of rows) {
      for (let col = 0; col < BOARD_COLS; col++) {
        const pos: GridPos = { col, row };
        if (this.callbacks.isCellAvailable(pos)) {
          candidates.push(pos);
        }
      }
    }

    if (candidates.length === 0) return null;
    return candidates[Phaser.Math.Between(0, candidates.length - 1)];
  }

  // ------------------------------------------------------------------
  // Weighted random piece selection
  // ------------------------------------------------------------------

  private rollPieceType(weights: SpawnWeights): PieceType {
    // Filter to only pieces introduced at or before current level (never King)
    const eligible = (Object.keys(weights) as PieceType[]).filter((pt) => {
      if (pt === 'King') return false;
      const cfg = PIECE_CONFIGS[pt];
      return cfg && cfg.introducedAtLevel <= this.currentLevel;
    });

    if (eligible.length === 0) {
      // Fallback — always allow Pawn
      return 'Pawn';
    }

    let totalWeight = 0;
    for (const pt of eligible) {
      totalWeight += weights[pt] ?? 0;
    }

    if (totalWeight === 0) return eligible[0];

    let roll = Math.random() * totalWeight;
    for (const pt of eligible) {
      roll -= weights[pt] ?? 0;
      if (roll <= 0) return pt;
    }

    return eligible[eligible.length - 1];
  }

  // ------------------------------------------------------------------
  // Public queries / ability hooks
  // ------------------------------------------------------------------

  /** Preview: what will the player's next piece be? */
  getNextPieceType(team: Team = 'player'): PieceType | null {
    const side = team === 'player' ? this.player : this.enemy;
    return side.nextPieceType;
  }

  /** Returns 0-1 fill fraction for the spawn bar UI. */
  getBarProgress(team: Team = 'player'): number {
    const side = team === 'player' ? this.player : this.enemy;
    if (side.blocked) return 1;
    return Math.min(side.elapsed / side.config.timerSeconds, 1);
  }

  /** Is the bar full but waiting for a cell? */
  isBlocked(team: Team = 'player'): boolean {
    const side = team === 'player' ? this.player : this.enemy;
    return side.blocked;
  }

  // --- Ability hooks ---

  /** Instantly fill the spawn bar and spawn now (Instant Spawn ability). */
  instantSpawn(team: Team = 'player'): void {
    const side = team === 'player' ? this.player : this.enemy;
    if (!side.nextPieceType) {
      side.nextPieceType = this.rollPieceType(side.config.weights);
    }
    const cell = this.findSpawnCell(side.team);
    if (cell) {
      this.doSpawn(side, cell);
    } else {
      side.blocked = true;
      side.pulseTime = 0;
    }
  }

  /** Reroll the previewed piece to a new random pick (Reroll ability). */
  rerollPreview(team: Team = 'player'): void {
    const side = team === 'player' ? this.player : this.enemy;
    side.nextPieceType = this.rollPieceType(side.config.weights);
  }

  // ------------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------------

  private makeSide(team: Team, config: SpawnConfig): SideSpawn {
    return {
      team,
      config: { ...config, weights: { ...config.weights } },
      elapsed: 0,
      nextPieceType: null,
      blocked: false,
      pulseTime: 0,
    };
  }
}
